/* eslint-disable react-refresh/only-export-components */
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        // NOTE: NSAI-6526 — `default` and `primary` are ONE fill, and it is
        // `--primary-dark`. They were two: `default` filled with
        // `--primary-dark` and `primary` with `--primary`, which in the dark
        // theme are `248.1 60.9% 56.9%` and `248 85% 78%` — a saturated blue
        // and a pale lavender. Both are primary actions, so the same screen
        // showed a filled button in two different brand colours depending on
        // which variant name the call site happened to reach for. The design
        // review named this directly ("the buttons differ in colour in the dark
        // version"), on a page carrying one of each.
        //
        // `--primary-dark` is the one that stays, on its own terms rather than
        // by headcount: NSAI-6259 chose it FOR this job — "the tour accent and
        // the primary-dark button variant follow it" — and it is the token that
        // ships a matching `--primary-dark-foreground`, so the label's contrast
        // is decided with the fill instead of being inherited from a token tuned
        // for text on a page. That it is also what ~690 of the ~715 filled
        // buttons already render is why unifying this way repaints 25 controls
        // rather than the whole app.
        //
        // Both names are kept, aliased, rather than deleting `primary` and
        // rewriting its call sites: the rename would be a large mechanical diff
        // through unrelated files for no behaviour change, and `variant="primary"`
        // reads correctly at a call site that means "this is the primary action".
        // They are declared separately, not merged into one key, so that the two
        // can diverge again deliberately if the palette ever gains a second
        // filled treatment — a divergence would then be a visible edit here.
        default:
          "bg-primary-dark text-primary-dark-foreground hover:bg-primary-dark/90",
        primary:
          "bg-primary-dark text-primary-dark-foreground hover:bg-primary-dark/90",
        // NOTE: NSAI-6640 — at night the destructive fill is the theme's red
        // taken ~22% towards black, with white text. The stock dark
        // `--destructive` is a bright `0 93% 59%` that its near-white
        // foreground only reaches 3.4:1 on, and a tenant palette that pairs its
        // red with dark text (the review's "Logout — dark text on red") left
        // the one dangerous action in the app as the only filled button without
        // white text. Mixing rather than a new token keeps each organization's
        // own hue: it is its red, made deep enough to carry white (≈5.5:1 on
        // the stock value). Light is unchanged.
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90 dark:bg-[color-mix(in_oklab,hsl(var(--destructive))_78%,black)] dark:text-white dark:hover:bg-[color-mix(in_oklab,hsl(var(--destructive))_70%,black)]",
        // NOTE: NSAI-6259 — hover answers with the BRAND, not with a grey.
        // `hover:bg-accent` is a near-neutral tint, so an outline button (the
        // integrations catalogue's Connect, among others) barely changed on
        // hover: a whole grid of them read as inert. The fill stays light
        // enough that the catalogue is not a wall of purple at rest — the
        // colour only appears under the pointer, which is where a state change
        // belongs. The border comes with it so the whole control moves, rather
        // than a fill appearing inside a grey outline.
        outline:
          "border border-input bg-background transition-colors hover:border-primary/40 hover:bg-primary/10 hover:text-primary dark:hover:bg-primary/15",
        "outline-transparent-primary":
          "border border-input bg-transparent hover:bg-accent hover:text-accent-foreground dark:border-primary",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        "ghost-destructive":
          "text-destructive hover:bg-destructive/10 hover:text-destructive",
        link: "text-primary underline-offset-4 hover:underline",
      },
      // NOTE: NSAI-6259 follow-up — one control height, 36px, shared with
      // `Input` and with the composer's own row. The default was 40px against
      // a 40px input and a 36px composer chip, so a form built from these
      // primitives never quite lined up and every row of buttons sat heavier
      // than the text beside it. `sm` stays a genuine step down rather than
      // collapsing onto the default.
      size: {
        default: "h-9 px-4 py-2",
        sm: "h-8 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-9 w-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface IButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, IButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
